import React, { useState, useEffect, useCallback } from 'react';
import { fmt, api } from '../utils/api';
import { categoryIcon } from '../utils/categoryStyle';

export default function BigPurchasesView({ onViewCharges, refreshKey }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.getBigPurchases(year);
      setData(d);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const years = [0, 1, 2].map((back) => new Date().getFullYear() - back);

  return (
    <div style={styles.wrap}>
      <div style={styles.intro}>
        <p style={styles.introText}>
          Every purchase {data ? fmt.currency(data.threshold) : '$1,000'}+ this year - vacations, big one-time
          costs, anything that isn't your normal week-to-week spending.
          {data?.excludedCategories?.length > 0 && (
            <> Excludes: {data.excludedCategories.join(', ')}.</>
          )}
        </p>
      </div>

      <div style={styles.controlsRow}>
        <div style={styles.pillGroup}>
          {years.map((y) => (
            <button
              key={y}
              style={{ ...styles.pill, ...(year === y ? styles.pillActive : {}) }}
              onClick={() => setYear(y)}
            >
              {y}
            </button>
          ))}
        </div>
        {data?.excludedFromBudget && (
          <span style={styles.excludedBadge}>✓ Pulled out of your budget pace</span>
        )}
      </div>

      {loading && !data ? (
        <p style={styles.empty}>Loading...</p>
      ) : !data || data.transactions.length === 0 ? (
        <p style={styles.empty}>No big purchases found for {year}.</p>
      ) : (
        <>
          <div style={styles.summaryCard}>
            <div style={styles.summaryTotal}>
              <span style={styles.summaryTotalLabel}>Total Big Purchases · {year}</span>
              <span style={styles.summaryTotalValue}>{fmt.currency(data.yearTotal)}</span>
            </div>
            <div style={styles.categoryGrid}>
              {data.categoryBreakdown.map((c) => (
                <div key={c.category} style={styles.categoryChip}>
                  <span style={styles.categoryChipIcon}>{categoryIcon(c.category)}</span>
                  <span style={styles.categoryChipName}>{c.category}</span>
                  <span style={styles.categoryChipCount}>×{c.count}</span>
                  <span style={styles.categoryChipTotal}>{fmt.currency(c.total)}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={styles.list}>
            {data.transactions.map((t) => (
              <button
                key={t.id}
                style={styles.row}
                onClick={() => onViewCharges?.(t.merchantName || t.description)}
              >
                <span style={styles.rowIcon}>{categoryIcon(t.category)}</span>
                <span style={styles.rowInfo}>
                  <span style={styles.rowName}>{t.merchantName || t.description}</span>
                  <span style={styles.rowMeta}>{fmt.shortDate(t.date)} · {t.category}</span>
                </span>
                <span style={styles.rowAmount}>{fmt.currency(t.amount)}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  wrap: { padding: '0' },
  intro: { marginBottom: '1rem' },
  introText: { color: '#6b7280', fontSize: '0.78rem', margin: 0, lineHeight: '1.5' },
  controlsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.6rem',
    marginBottom: '1.25rem',
  },
  pillGroup: { display: 'flex', gap: '0.4rem' },
  pill: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#9ca3af',
    borderRadius: '999px',
    padding: '5px 14px',
    fontSize: '0.72rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  pillActive: {
    background: '#ef4444',
    color: '#fff',
    border: '1px solid #ef4444',
  },
  excludedBadge: {
    color: '#00d4aa',
    fontSize: '0.7rem',
    fontWeight: '600',
    fontFamily: "'Syne', sans-serif",
  },
  empty: { color: '#4b5563', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' },
  summaryCard: {
    background: 'rgba(239, 68, 68, 0.05)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '12px',
    padding: '1rem 1.1rem',
    marginBottom: '1rem',
  },
  summaryTotal: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: '0.85rem',
    paddingBottom: '0.75rem',
    borderBottom: '1px solid rgba(239, 68, 68, 0.2)',
  },
  summaryTotalLabel: { color: '#9ca3af', fontSize: '0.78rem', fontFamily: "'Syne', sans-serif" },
  summaryTotalValue: { color: '#ef4444', fontSize: '1.4rem', fontWeight: '800', fontFamily: "'DM Mono', monospace" },
  categoryGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  categoryChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '999px',
    padding: '5px 10px',
  },
  categoryChipIcon: { fontSize: '0.8rem' },
  categoryChipName: { color: '#e5e7eb', fontSize: '0.72rem', fontWeight: '600', fontFamily: "'Syne', sans-serif" },
  categoryChipCount: { color: '#4b5563', fontSize: '0.65rem', fontFamily: "'DM Mono', monospace" },
  categoryChipTotal: { color: '#ef4444', fontSize: '0.72rem', fontWeight: '700', fontFamily: "'DM Mono', monospace" },
  list: { display: 'flex', flexDirection: 'column', gap: '4px' },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderLeft: '3px solid #ef4444',
    borderRadius: '8px',
    padding: '0.6rem 0.75rem',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
  },
  rowIcon: { fontSize: '1rem', flexShrink: 0 },
  rowInfo: { flex: 1, display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 },
  rowName: {
    color: '#e5e7eb',
    fontSize: '0.82rem',
    fontWeight: '600',
    fontFamily: "'Syne', sans-serif",
  },
  rowMeta: {
    color: '#6b7280',
    fontSize: '0.7rem',
    fontFamily: "'DM Mono', monospace",
  },
  rowAmount: {
    color: '#ef4444',
    fontSize: '0.9rem',
    fontWeight: '800',
    fontFamily: "'DM Mono', monospace",
    flexShrink: 0,
  },
};
